import type { ApiError } from '@/lib/api/errors'

import type {
  VenueCampaignCreateInput,
  VenueCampaignDetailView,
  VenueCampaignEstimateView,
  VenueCampaignSendSlot,
  VenueCampaignUpdateInput,
} from './campaignTypes'
import type { PromotionEstimateParams } from './campaignViewModel'
import { EMPTY_MARK } from './format'
import { citiesForState, regionCityName, regionStateName } from './regionCodes'

/**
 * 지역 홍보 작성/이어서 작성 전용 순수 함수(설계 §2 — NOTICE 전용 `campaignViewModel.ts`의
 * `ComposeFormState`와 병렬로 둔다, REGION 대상·이미지·링크·예약 시각은 NOTICE에 없는 개념).
 */

const TITLE_MAX_LENGTH = 100
const BODY_MAX_LENGTH = 1000
const LINK_URL_MAX_LENGTH = 500
const LINK_URL_PATTERN = /^https?:\/\/\S+$/

const KST_WALL_CLOCK_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

/** KST(Asia/Seoul) 기준 "지금"을 `POST campaigns`의 `scheduledAt`과 같은 "yyyy-MM-ddTHH:mm" 꼴로. */
export function nowWallClockKst(now: Date = new Date()): string {
  const parts = Object.fromEntries(
    KST_WALL_CLOCK_FMT.formatToParts(now)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value])
  )
  const hour = parts.hour === '24' ? '00' : parts.hour
  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`
}

export type PromotionSendMode = 'IMMEDIATE' | 'SCHEDULED'

/** 작성 화면 폼 상태 — `POST campaigns`(#714 §1-1, `type:'PROMOTION'`)의 부분집합. */
export interface PromotionComposeFormState {
  title: string
  body: string
  /** 업로드 완료 후 채워짐(`null` = 미첨부). */
  imageUrl: string | null
  linkUrl: string
  regionStateId: string | null
  /** `null` = 시/도 전체. */
  regionCityId: string | null
  sendSlot: VenueCampaignSendSlot | null
  sendMode: PromotionSendMode
  /** `SCHEDULED` 전용, "yyyy-MM-dd". */
  scheduledDate: string
  /** `SCHEDULED` 전용, "HH:mm". */
  scheduledTime: string
}

export function initialPromotionComposeFormState(): PromotionComposeFormState {
  return {
    title: '',
    body: '',
    imageUrl: null,
    linkUrl: '',
    regionStateId: null,
    regionCityId: null,
    sendSlot: null,
    sendMode: 'IMMEDIATE',
    scheduledDate: '',
    scheduledTime: '',
  }
}

export interface PromotionComposeFormErrors {
  title?: string
  body?: string
  linkUrl?: string
  region?: string
  scheduledAt?: string
}

export function hasPromotionComposeFormErrors(errors: PromotionComposeFormErrors): boolean {
  return Object.values(errors).some((value) => Boolean(value))
}

/**
 * #714 §1-1·§3 규약 미러링(최종 판정은 서버 `40050`). `nowWallClock`은 "yyyy-MM-ddTHH:mm"(KST) —
 * 순수 함수로 유지하려고 `Date.now()`를 직접 쓰지 않고 호출부가 넘긴다.
 */
export function validatePromotionComposeForm(
  form: PromotionComposeFormState,
  nowWallClock: string
): PromotionComposeFormErrors {
  const errors: PromotionComposeFormErrors = {}

  if (form.title.trim().length === 0) errors.title = '제목을 입력해 주세요.'
  else if (form.title.length > TITLE_MAX_LENGTH) errors.title = `제목은 ${TITLE_MAX_LENGTH}자 이내로 입력해 주세요.`

  if (form.body.trim().length === 0) errors.body = '내용을 입력해 주세요.'
  else if (form.body.length > BODY_MAX_LENGTH) errors.body = `내용은 ${BODY_MAX_LENGTH}자 이내로 입력해 주세요.`

  const linkUrl = form.linkUrl.trim()
  if (linkUrl.length > 0) {
    if (!LINK_URL_PATTERN.test(linkUrl)) errors.linkUrl = 'http(s):// 로 시작하는 링크를 입력해 주세요.'
    else if (linkUrl.length > LINK_URL_MAX_LENGTH) errors.linkUrl = `링크는 ${LINK_URL_MAX_LENGTH}자 이내로 입력해 주세요.`
  }

  if (!form.regionStateId) errors.region = '발송 지역을 선택해 주세요.'

  if (form.sendMode === 'SCHEDULED') {
    if (!form.scheduledDate || !form.scheduledTime) {
      errors.scheduledAt = '예약 발송 일시를 입력해 주세요.'
    } else if (`${form.scheduledDate}T${form.scheduledTime}` <= nowWallClock) {
      errors.scheduledAt = '예약 시각은 현재보다 이후여야 합니다.'
    }
  }

  return errors
}

/** 지역(시/도) 변경 시 시/군구 선택을 초기화한다 — 이전 시/도의 시/군구 코드가 남지 않게. */
export function changePromotionRegionState(
  form: PromotionComposeFormState,
  regionStateId: string
): PromotionComposeFormState {
  return { ...form, regionStateId, regionCityId: null }
}

/** 대상 산출(`GET campaigns/estimate`) 쿼리 파라미터 — 지역·시간대가 바뀔 때만 값이 바뀐다(제목·본문·이미지는 트리거 아님). */
export function toPromotionEstimateParams(form: PromotionComposeFormState): PromotionEstimateParams {
  return {
    type: 'PROMOTION',
    targetType: 'REGION',
    regionStateId: form.regionStateId ?? '',
    regionCityId: form.regionCityId ?? undefined,
    sendSlot: form.sendSlot ?? undefined,
  }
}

/** 지역이 아직 선택되지 않았으면 대상 산출을 트리거하지 않는다(빈 `regionStateId`로 400을 유발하지 않는다). */
export function promotionEstimateParamsReady(form: PromotionComposeFormState): boolean {
  return Boolean(form.regionStateId)
}

/** `SCHEDULED` 모드 + 날짜·시각이 모두 채워졌을 때만 값을 만든다(그 외는 `null`). */
export function toPromotionScheduledAt(form: PromotionComposeFormState): string | null {
  if (form.sendMode !== 'SCHEDULED') return null
  if (!form.scheduledDate || !form.scheduledTime) return null
  return `${form.scheduledDate}T${form.scheduledTime}`
}

/**
 * 폼 → `POST campaigns` 요청 본문.
 * 🚨 `intent:'DRAFT'`면 폼의 발송 모드와 무관하게 `scheduledAt`을 항상 `null`로 보낸다 — "임시 저장"은
 * 사용자가 아직 발송 시점을 확정하지 않겠다는 의도이므로 예약 모드를 골라 둔 상태에서 임시 저장해도
 * DRAFT로 남는다(설계 §API 흐름).
 */
export function toPromotionCreateInput(
  form: PromotionComposeFormState,
  intent: 'DRAFT' | 'COMMIT'
): VenueCampaignCreateInput {
  return {
    type: 'PROMOTION',
    title: form.title.trim(),
    body: form.body.trim(),
    imageUrl: form.imageUrl,
    linkUrl: form.linkUrl.trim() || null,
    target: {
      targetType: 'REGION',
      reservationFromDate: null,
      reservationToDate: null,
      courtIds: null,
      regionStateId: form.regionStateId,
      regionCityId: form.regionCityId,
      sendSlot: form.sendSlot,
    },
    scheduledAt: intent === 'DRAFT' ? null : toPromotionScheduledAt(form),
  }
}

/** 폼 → `PUT campaigns/{id}` 요청 본문 — 제목·본문·이미지·링크만(대상·예약 시각은 이 EP로 바뀌지 않는다). */
export function toPromotionUpdateInput(form: PromotionComposeFormState): VenueCampaignUpdateInput {
  return {
    title: form.title.trim(),
    body: form.body.trim(),
    imageUrl: form.imageUrl,
    linkUrl: form.linkUrl.trim() || null,
  }
}

/**
 * "이어서 작성" 진입 — 기존 DRAFT 상세를 폼 초기값으로 변환한다.
 * 🚨 대상 조건(`regionStateId`/`regionCityId`/`sendSlot`)은 작성 시점에 동결돼 있으므로 그대로
 * 채우되 화면은 읽기 전용으로 렌더한다(`PromotionTargetPicker`의 `readOnly` prop). DRAFT는
 * `scheduledAt`이 항상 `null`인 불변식이라 발송 모드는 매번 `IMMEDIATE`로 재선택하게 시작한다.
 */
export function promotionDetailToFormState(detail: VenueCampaignDetailView): PromotionComposeFormState {
  return {
    title: detail.summary.title,
    body: detail.body,
    imageUrl: detail.imageUrl,
    linkUrl: detail.linkUrl ?? '',
    regionStateId: detail.target.regionStateId,
    regionCityId: detail.target.regionCityId,
    sendSlot: detail.target.sendSlot,
    sendMode: 'IMMEDIATE',
    scheduledDate: '',
    scheduledTime: '',
  }
}

export const SEND_SLOT_OPTIONS: { value: VenueCampaignSendSlot; label: string }[] = [
  { value: 'MORNING', label: '오전' },
  { value: 'AFTERNOON', label: '오후' },
  { value: 'EVENING', label: '저녁' },
]

const SEND_SLOT_LABEL: Record<VenueCampaignSendSlot, string> = {
  MORNING: '오전',
  AFTERNOON: '오후',
  EVENING: '저녁',
}

/**
 * 작성/확인 모달 공용 대상 요약 문구 — 서버가 옵션 목록을 주지 않는 시점(작성 중)이라 정적 코드표
 * (`regionCodes.ts`)의 이름을 쓴다. 캠페인 생성 후 상세·이력 화면은 서버가 준
 * `target.regionStateName`/`sendSlotLabel`(`targetViewSummaryText`)을 그대로 쓴다 — 이 함수와 다른
 * 원천을 섞지 않는다.
 */
export function promotionTargetSummaryText(form: PromotionComposeFormState): string {
  const stateName = regionStateName(form.regionStateId)
  if (!stateName) return EMPTY_MARK

  const cityName = regionCityName(form.regionStateId, form.regionCityId)
  const region = cityName ? `${stateName} ${cityName}` : `${stateName} 전체`
  const slotLabel = form.sendSlot ? SEND_SLOT_LABEL[form.sendSlot] : null
  return slotLabel ? `${region} · ${slotLabel}` : region
}

/** `PromotionTargetPicker`의 시/군구 옵션 — 시/도가 없으면 빈 배열. */
export function promotionCityOptions(regionStateId: string | null) {
  return citiesForState(regionStateId)
}

/**
 * 발송 확인 모달의 확정 안내 5항목(요구사항 3 — "수정·취소 불가 / 실제 발송 건수 과금 / 동의자만 발송 /
 * 시간 제한 / 일 수신 한도 초과자 제외"). 시간 제한·일 수신 한도는 `estimate.sendPolicy` 실값을
 * 보간한다(정책 미설정으로 `estimate`가 아직 없으면 일반 문구로 대체).
 */
export function promotionConfirmGuideLines(estimate: VenueCampaignEstimateView | undefined): string[] {
  const sendPolicy = estimate?.sendPolicy
  const windowText = sendPolicy
    ? `${sendPolicy.sendWindowStartHour}시~${sendPolicy.sendWindowEndHour}시`
    : '정책에 설정된 시간대'
  const dailyLimitText = sendPolicy ? `일 ${sendPolicy.dailyLimitPerMember}회` : '정책에 설정된 한도'

  return [
    '확정 후에는 내용을 수정하거나 취소할 수 없습니다.',
    '실제 발송 건수를 기준으로 비용이 확정됩니다.',
    '광고 수신에 동의한 사용자에게만 발송됩니다(미동의·철회 회원은 자동 제외됩니다).',
    `${windowText}에만 발송할 수 있습니다.`,
    `${dailyLimitText}를 초과해 수신한 사용자는 이번 발송에서 자동 제외됩니다.`,
  ]
}

/** `blockRetryable`별 CTA 문구 — `OUTSIDE_SEND_WINDOW`만 재시도 가능(#731 §6). */
export function promotionBlockCtaLabel(blockRetryable: boolean | null): string {
  return blockRetryable ? '다시 시도' : '운영자 문의'
}

/** `POST campaigns`의 작성 시점 발송 창 차단(#731 §1, `errorCode=40939`). */
export const SEND_WINDOW_BLOCKED_ERROR_CODE = 40939

/**
 * `40939`(발송 가능 창 밖·정책 미설정, 작성 시점 fail-closed)를 `40913`(기능 차단)과 구분한다.
 * 🚨 판별 기준은 `blocked` 봉투의 부재다 — `40939` 응답에는 `blockReason`(기능 차단 전용 필드)이
 * 없어 `ApiError.blocked`가 `undefined`로 떨어진다(설계 §자율 결정 4).
 */
export function isSendWindowBlockedError(error: ApiError): boolean {
  return error.isConflict && !error.blocked && error.code === SEND_WINDOW_BLOCKED_ERROR_CODE
}
